import {
  SphereGeometry,
  ShaderMaterial,
  Mesh,
  BackSide,
  InstancedMesh,
  Object3D,
  MeshBasicMaterial,
  Color,
  Matrix4,
} from 'three';
import { VISUAL, COLORS } from '../constants';
import nebulaVert from '../shaders/nebula.vert?raw';
import nebulaFrag from '../shaders/nebula.frag?raw';

export class EnvironmentRenderer {
  readonly nebulaMesh: Mesh;
  readonly particleMesh: InstancedMesh;
  private nebulaMaterial: ShaderMaterial;
  private particleData: { vx: number; vy: number; vz: number }[] = [];
  private dummy = new Object3D();

  constructor() {
    // Nebula background sphere (inside-out rendering)
    const nebulaGeo = new SphereGeometry(200, 64, 64);
    this.nebulaMaterial = new ShaderMaterial({
      vertexShader: nebulaVert,
      fragmentShader: nebulaFrag,
      uniforms: {
        uTime: { value: 0 },
      },
      side: BackSide,
      depthWrite: false,
    });
    this.nebulaMesh = new Mesh(nebulaGeo, this.nebulaMaterial);

    // Ambient particles (instanced small spheres)
    const particleGeo = new SphereGeometry(0.03, 4, 4);
    const particleMat = new MeshBasicMaterial({
      color: new Color(COLORS.healthy),
      transparent: true,
      opacity: 0.4,
    });

    const count = VISUAL.particleCount;
    this.particleMesh = new InstancedMesh(particleGeo, particleMat, count);

    // Initialize particle positions in a sphere around the platform
    for (let i = 0; i < count; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = 10 + Math.random() * 50;

      const x = r * Math.sin(phi) * Math.cos(theta);
      const y = r * Math.sin(phi) * Math.sin(theta) - 5;
      const z = r * Math.cos(phi);

      this.dummy.position.set(x, y, z);
      const scale = 0.5 + Math.random() * 1.5;
      this.dummy.scale.setScalar(scale);
      this.dummy.updateMatrix();
      this.particleMesh.setMatrixAt(i, this.dummy.matrix);

      this.particleData.push({
        vx: (Math.random() - 0.5) * 0.02,
        vy: (Math.random() - 0.5) * 0.01,
        vz: (Math.random() - 0.5) * 0.02,
      });
    }
    this.particleMesh.instanceMatrix.needsUpdate = true;
  }

  private tempMatrix = new Matrix4();
  private frameCounter = 0;

  update(time: number): void {
    this.nebulaMaterial.uniforms.uTime.value = time;

    // Only update particles every 3rd frame (huge perf win, barely visible)
    this.frameCounter++;
    if (this.frameCounter % 3 !== 0) return;

    const mat = this.tempMatrix;
    const count = VISUAL.particleCount;
    for (let i = 0; i < count; i++) {
      this.particleMesh.getMatrixAt(i, mat);

      mat.elements[12] += this.particleData[i].vx;
      mat.elements[13] += this.particleData[i].vy;
      mat.elements[14] += this.particleData[i].vz;

      this.particleMesh.setMatrixAt(i, mat);
    }
    this.particleMesh.instanceMatrix.needsUpdate = true;
  }
}
